import React, { createContext, useContext, useState, useCallback } from 'react';

const LoadingContext = createContext();

export const useLoadingBar = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoadingBar must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [loadingTasks, setLoadingTasks] = useState(new Map());
  const [isVisible, setIsVisible] = useState(false);

  // Register a loading task
  const registerTask = useCallback((taskId, initialProgress = 0) => {
    setLoadingTasks(prev => {
      const newTasks = new Map(prev);
      newTasks.set(taskId, {
        progress: initialProgress,
        weight: 1, // Default weight
        message: '',
        startTime: Date.now()
      });
      return newTasks;
    });
    setIsVisible(true);
  }, []);

  // Update task progress
  const updateTask = useCallback((taskId, progress, message = '') => {
    setLoadingTasks(prev => {
      const newTasks = new Map(prev);
      const task = newTasks.get(taskId);
      if (task) {
        newTasks.set(taskId, {
          ...task,
          progress: Math.max(0, Math.min(100, progress)),
          message: message || task.message
        });
      }
      return newTasks;
    });
  }, []);

  // Complete a task
  const completeTask = useCallback((taskId) => {
    setLoadingTasks(prev => {
      const newTasks = new Map(prev);
      newTasks.delete(taskId);
      return newTasks;
    });
  }, []);

  // Set task weight (for different loading priorities)
  const setTaskWeight = useCallback((taskId, weight) => {
    setLoadingTasks(prev => {
      const newTasks = new Map(prev);
      const task = newTasks.get(taskId);
      if (task) {
        newTasks.set(taskId, { ...task, weight });
      }
      return newTasks;
    });
  }, []);

  // Calculate aggregate progress
  const getAggregateProgress = useCallback(() => {
    if (loadingTasks.size === 0) return 0;
    
    let totalWeightedProgress = 0;
    let totalWeight = 0;
    
    for (const [taskId, task] of loadingTasks) {
      totalWeightedProgress += task.progress * task.weight;
      totalWeight += task.weight;
    }
    
    return totalWeight > 0 ? totalWeightedProgress / totalWeight : 0;
  }, [loadingTasks]);

  // Get current loading message
  const getCurrentMessage = useCallback(() => {
    if (loadingTasks.size === 0) return '';
    
    // Find the task with highest progress that's not complete
    let highestProgress = 0;
    let currentMessage = '';
    
    for (const [taskId, task] of loadingTasks) {
      if (task.progress > highestProgress && task.progress < 100) {
        highestProgress = task.progress;
        currentMessage = task.message;
      }
    }
    
    return currentMessage;
  }, [loadingTasks]);

  // Check if any tasks are loading
  const isLoading = loadingTasks.size > 0;

  // Hide loading bar when all tasks complete
  React.useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => setIsVisible(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const value = {
    registerTask,
    updateTask,
    completeTask,
    setTaskWeight,
    getAggregateProgress,
    getCurrentMessage,
    isLoading,
    isVisible,
    taskCount: loadingTasks.size
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};
